import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scissors } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-24 pb-12" data-testid="login-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <Scissors className="w-5 h-5 text-[#d4af37]" strokeWidth={1.5} />
          <span className="font-display text-lg text-white">The Barbers <span className="italic text-[#d4af37]">Chair</span></span>
        </div>
        <h1 className="font-display text-4xl text-white mb-2">Welcome back</h1>
        <p className="text-white/50 font-body mb-10">Sign in to manage your appointments.</p>

        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors" data-testid="login-email" />
          </div>
          <div>
            <label className="overline text-[0.6rem] text-white/50 block mb-2">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent border-b border-white/20 py-3 text-white font-body focus:border-[#d4af37] focus:outline-none transition-colors" data-testid="login-password" />
          </div>
          {error && <p className="text-red-400 text-sm font-body" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} className="group w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black py-4 overline text-xs hover:bg-[#c5a028] disabled:opacity-50 transition-colors" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign In"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </form>

        <p className="text-white/50 font-body text-sm mt-8">
          New here? <Link to="/signup" className="text-[#d4af37] underline underline-offset-4" data-testid="to-signup">Create an account</Link>
        </p>
        <p className="text-white/30 font-body text-xs mt-3">Or just <Link to="/book" className="text-[#d4af37]">book as a guest</Link>.</p>
      </motion.div>
    </div>
  );
}
